const LINE_ENDING_REGEX = /\r?\n/;
const HUNK_HEADER_REGEX = /^@@.+@@(.*)?$/;

type ChangeType = 'delete' | 'insert' | 'context';

type PendingChange = { type: ChangeType; line: string };

type PendingChanges = PendingChange[];

type UnappliedChanges = Array<{ type: Exclude<ChangeType, 'context'>; line: string }>;

type SlidingWindowStrategy = 'start' | 'end' | 'both';

interface Callbacks {
  invalidHunk?: (line: string) => void;
}

export class StreamingDiffParser {
  #lines: string[];
  #generator?: Generator<void, void, string>;
  #done = false;
  #buffer = '';
  #callbacks?: Callbacks;
  #unappliedChanges: UnappliedChanges[] = [];
  #pendingChanges: PendingChanges = [];
  #appliedChanges = false;

  get pendingChanges(): Readonly<PendingChanges> {
    return this.#pendingChanges.filter(({ type }) => type !== 'context');
  }

  constructor(
    public originalFileContent: string,
    callbacks?: Callbacks,
  ) {
    this.#lines = !originalFileContent ? [] : originalFileContent.split(LINE_ENDING_REGEX);
    this.#callbacks = callbacks;
  }

  parse(diff: string, isEnd: boolean) {
    if (this.#done) {
      return this.#getFileContent();
    }

    /**
     * If the diff is fully streamed then we need to add a final newline because
     * otherwise the parser won't treat the last line as "complete" since it
     * operates line by line. Note, the final line will only be processed with
     * another call to `parse`.
     */
    if (isEnd && !diff.endsWith('\n')) {
      diff += '\n';
    }

    this.#generator ??= this.#parse();

    this.#buffer = diff;

    const result = this.#generator.next(diff);

    if (result.done || isEnd) {
      this.#done = true;
    }

    if (isEnd && this.pendingChanges.length > 0) {
      // try to apply any remaining pending changes
      const result = this.#tryApplyChanges(this.#pendingChanges, 0, true);

      if (result) {
        this.#pendingChanges = [];
      } else {
        this.#trackUnappliedChanges(true);
      }
    }

    return this.#getFileContent();
  }

  finalize() {
    this.#trackUnappliedChanges(true);

    return {
      unappliedChanges: this.#unappliedChanges,
    };
  }

  *#parse(): Generator<void, void, string> {
    let currentHunkPosition = 0;
    let diffPosition = 0;
    let seenFirstHunk = false;

    while (true) {
      const { line, newlineIndex } = yield* this.#nextLine(diffPosition);

      diffPosition = newlineIndex;

      // skip lines until we see the first hunk header
      if (!seenFirstHunk) {
        if (line.startsWith('@@') && HUNK_HEADER_REGEX.test(line)) {
          seenFirstHunk = true;
        } else {
          continue;
        }
      }

      const type = line[0];
      const trimmedLine = line.slice(1);

      let pendingChange: PendingChange | undefined;

      const lineExists = this.#lineExists(line);
      const trimmedLineExists = this.#lineExists(trimmedLine);

      if (type === '@') {
        if (line[1] !== '@' && this.#lineExists(line)) {
          // if it's not a valid hunk and the line exists in the file we treat it as context
          pendingChange = { type: 'context', line };
        } else {
          if (!HUNK_HEADER_REGEX.test(line)) {
            this.#callbacks?.invalidHunk?.(line);
          }

          if (this.#pendingChanges.length > 0) {
            // before starting a new hunk, try to apply any pending changes
            const result = this.#tryApplyChanges(this.#pendingChanges, currentHunkPosition, true);

            if (!result) {
              this.#trackUnappliedChanges(false);
            }
          }

          currentHunkPosition = 0;

          this.#appliedChanges = false;
          this.#pendingChanges = [];
        }
      } else if (type === '-') {
        if (!lineExists) {
          pendingChange = { type: 'delete', line: trimmedLine };
        } else if (!trimmedLineExists) {
          if (lineExists) {
            pendingChange = { type: 'context', line };
          } else {
            // hallucinated line
          }
        } else {
          /**
           * At this point it's impossible to know whether it's context or
           * an actual deletion so we assume that it's correct and interpret
           * it as a deletion.
           */
          pendingChange = { type: 'delete', line: trimmedLine };
        }
      } else if (type === '+') {
        if (this.#pendingChanges.length === 0) {
          pendingChange = { type: 'insert', line: trimmedLine };
        } else {
          const previousContextLines = this.#getPreviousContextLines();
          const positions = this.#findMatchingPositions(currentHunkPosition, [...previousContextLines, line]);

          if (positions.length > 0) {
            pendingChange = { type: 'context', line };
          } else {
            pendingChange = { type: 'insert', line: trimmedLine };
          }
        }
      } else if (type === ' ') {
        if (lineExists || trimmedLineExists) {
          const potentialContextLine = trimmedLineExists ? trimmedLine : line;

          pendingChange = { type: 'context', line: potentialContextLine };
        } else {
          /**
           * This line appears to be hallucinated since neither the full line
           * nor the trimmed version exists in the source. However, we still add
           * the trimmed line as context, as omitting it could lead to unexpected
           * changes, even though this may result in some changes not being applied.
           */
          pendingChange = { type: 'context', line: trimmedLine };
        }
      } else {
        /**
         * Treat all other existing lines as context. While context lines should always be prefixed
         * with a space, the model sometimes omits this prefix. To make the parser more resilient to such
         * mistakes, we consider any line without a recognized prefix as context. Note that this approach
         * isn't perfect - if an unchanged line starts with '+' or '-', it will be incorrectly treated
         * as a change. However, this edge case is unavoidable given the current implementation.
         */
        if (lineExists) {
          pendingChange = { type: 'context', line };
        } else {
          // hallucinated line and it makes no sense to use it as context
        }
      }

      if (pendingChange) {
        this.#pendingChanges.push(pendingChange);
      }

      const result = this.#tryApplyChanges(this.#pendingChanges, currentHunkPosition, false);

      if (result) {
        currentHunkPosition = result.newPosition;
        this.#appliedChanges = true;
        this.#pendingChanges = [];
      }
    }
  }

  #getFileContent(): string {
    // regardless of the original line endings, the output will use consistent LF (\n) line endings
    return this.#lines.join('\n');
  }

  #tryApplyChanges(changes: PendingChanges, startLineIndex: number, finalize: boolean) {
    const { hasChanges, deletions, additions, contextLines } = groupChanges(changes);

    if (!hasChanges) {
      return undefined;
    }

    /**
     * If we have additions but no context lines, and we're at the start of a non-empty
     * file with no changes applied yet, skip the changes until we have some more context.
     * This prevents incorrectly inserting changes at the start of the file when they should
     * go elsewhere.
     */
    if (
      additions.length > 0 &&
      contextLines.length === 0 &&
      this.#lines.length > 0 &&
      startLineIndex === 0 &&
      !this.#appliedChanges
    ) {
      return undefined;
    }

    /**
     * If we have deletions but no additions we wait for more context. The `finalize` flag
     * is only supposed to be true to when we see the beginning of a hunk or if we are at
     * the end of the diff to force applying the pending changes.
     */
    if (!finalize && deletions.length > 0 && additions.length === 0) {
      return undefined;
    }

    if (contextLines.length === 0) {
      return this.#applyChangesAtPosition(changes, startLineIndex);
    }

    let positions = this.#findMatchingPositions(startLineIndex, contextLines);

    if (positions.length === 0) {
      // split context into before and after changes
      const { contextBefore, contextAfter } = this.#splitContextAroundChanges(changes);

      // try matching with context before changes
      if (contextBefore.length > 0) {
        positions = this.#findMatchingPositions(startLineIndex, contextBefore);
      }

      // if still no match, try context after changes
      if (positions.length === 0 && contextAfter.length > 0) {
        positions = this.#findMatchingPositions(startLineIndex, contextAfter);

        // move position back by 1 to insert changes before the matching context
        if (positions.length === 1) {
          /**
           * If we have matched with context after then we need to remove the context before
           * otherwise it will move the line index and the changes would be applied to the
           * wrong location.
           */
          positions = [positions[0] - contextBefore.length];
        }
      }

      /**
       * If we still have no match, try sliding window approaches by progressively removing
       * elements from different positions until we find a match or run out of context.
       */
      if (positions.length === 0 && contextLines.length > 0) {
        // try removing elements from the beginning
        let result = this.#trySlidingWindow('start', contextLines, startLineIndex, changes);

        if (result) {
          return result;
        }

        // try removing elements from the end
        result = this.#trySlidingWindow('end', contextLines, startLineIndex, changes);

        if (result) {
          return result;
        }

        // try removing elements from both start and end
        result = this.#trySlidingWindow('both', contextLines, startLineIndex, changes);

        if (result) {
          return result;
        }
      }
    } else if (positions.length > 1 && this.#appliedChanges) {
      const { contextBefore, contextAfter } = this.#splitContextAroundChanges(changes);

      if (this.#matchContext(startLineIndex, contextBefore) && this.#matchContext(startLineIndex, contextAfter)) {
        return this.#applyChangesAtPosition(changes, startLineIndex);
      }
    }

    if (positions.length === 1) {
      return this.#applyChangesAtPosition(changes, positions[0]);
    }

    // still ambiguous
    return undefined;
  }

  #splitContextAroundChanges(changes: PendingChanges): { contextBefore: string[]; contextAfter: string[] } {
    const contextBefore: string[] = [];
    const contextAfter: string[] = [];

    let foundChange = false;

    for (const change of changes) {
      if (change.type !== 'insert') {
        if (!foundChange) {
          contextBefore.push(change.line);
        } else {
          contextAfter.push(change.line);
        }
      } else {
        foundChange = true;
      }
    }

    return { contextBefore, contextAfter };
  }

  #findMatchingPositions(startLineIndex: number, linesToMatch: string[]): number[] {
    if (linesToMatch.length === 0) {
      return [startLineIndex];
    }

    const positions: number[] = [];

    for (let i = startLineIndex; i < this.#lines.length; i++) {
      if (this.#matchContext(i, linesToMatch)) {
        positions.push(i);
      }
    }

    return positions;
  }

  #matchContext(startIndex: number, contextLines: string[]): boolean {
    if (startIndex + contextLines.length > this.#lines.length) {
      return false;
    }

    return contextLines.every((contextLine, i) => {
      return this.#lines[startIndex + i] === contextLine;
    });
  }

  #applyChangesAtPosition(changes: PendingChanges, position: number) {
    let currentLineIndex = position;

    for (const change of changes) {
      switch (change.type) {
        case 'context': {
          currentLineIndex++;
          break;
        }
        case 'delete': {
          this.#lines.splice(currentLineIndex, 1);
          break;
        }
        case 'insert': {
          this.#lines.splice(currentLineIndex, 0, change.line);
          currentLineIndex++;
          break;
        }
      }
    }

    return { newPosition: currentLineIndex };
  }

  *#nextLine(position: number): Generator<void, { line: string; newlineIndex: number }, string> {
    let newlineIndex = this.#buffer.indexOf('\n', position);

    while (newlineIndex === -1) {
      this.#buffer = yield;
      newlineIndex = this.#buffer.indexOf('\n', position);
    }

    return {
      line: this.#buffer.slice(position, newlineIndex),
      newlineIndex: newlineIndex + 1,
    };
  }

  #lineExists(line: string) {
    return this.#lines.some((currentLine) => currentLine === line);
  }

  #getPreviousContextLines() {
    return this.#pendingChanges.flatMap((change) => {
      if (change.type !== 'insert') {
        return change.line;
      }

      return [];
    });
  }

  #trackUnappliedChanges(eof: boolean) {
    const unappliedChanges = this.#pendingChanges.filter(({ type }) => type !== 'context') as UnappliedChanges;

    if (unappliedChanges.length === 0) {
      return;
    }

    const lastUnappliedChanges = this.#unappliedChanges.at(-1);

    if (eof && lastUnappliedChanges) {
      lastUnappliedChanges.push(...unappliedChanges);
    } else {
      this.#unappliedChanges.push(unappliedChanges);
    }

    this.#pendingChanges = [];
  }

  #trySlidingWindow(
    strategy: SlidingWindowStrategy,
    contextLines: string[],
    startLineIndex: number,
    changes: PendingChanges,
  ) {
    if (contextLines.length === 0) {
      return undefined;
    }

    const maxIterations = strategy === 'both' ? Math.floor(contextLines.length / 2) : contextLines.length - 1;

    for (let i = 1; i <= maxIterations; i++) {
      let slidingContext: string[];
      let positionAdjustment = 0;

      if (strategy === 'start') {
        // remove from the beginning
        slidingContext = contextLines.slice(i);
        positionAdjustment = -i;
      } else if (strategy === 'end') {
        // remove from the end
        slidingContext = contextLines.slice(0, -i);
        positionAdjustment = 0;
      } else {
        // remove from both ends
        slidingContext = contextLines.slice(i, -i);
        positionAdjustment = -i;
      }

      if (slidingContext.length === 0) {
        break;
      }

      const positions = this.#findMatchingPositions(startLineIndex, slidingContext);

      if (positions.length === 1) {
        return this.#applyChangesAtPosition(changes, positions[0] + positionAdjustment);
      }
    }

    return undefined;
  }
}

function groupChanges(changes: PendingChanges) {
  const deletions: PendingChange[] = [];
  const additions: PendingChange[] = [];
  const contextLines: string[] = [];

  let hasChanges = false;

  for (const change of changes) {
    if (change.type === 'delete') {
      deletions.push(change);
      contextLines.push(change.line);
      hasChanges = true;
    } else if (change.type === 'insert') {
      additions.push(change);
      hasChanges = true;
    } else {
      // context
      contextLines.push(change.line);
    }
  }

  return { deletions, additions, contextLines, hasChanges };
}
